'use client'

import React, { useContext, useEffect, useRef, useState } from 'react'
import { SectionThemeContext } from '../../../contexts/SectionTheme.context'
import { Heading } from '../../atoms/Typography/Typography.component'
import Button from '../../atoms/Button/Button.component'
import * as S from './StickyCta.styles'
import { ColorSchemeTypes } from '../../../types/ColorScheme'
import { RawStickyCtaProps } from '../../../models/organisms/StickyCta'

function StickyCta({
  type,
  title,
  cta,
  colorScheme,
  scrollThreshold = 500,
}: RawStickyCtaProps) {
  const { selectedColorScheme } = useContext(SectionThemeContext)
  const activeColorScheme = colorScheme || selectedColorScheme
  const wrapperRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const [isExpanded, setIsExpanded] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleScroll = () => {
      const scrollY = window.scrollY
      setIsExpanded(scrollY > scrollThreshold)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [scrollThreshold])

  useEffect(() => {
    if (!wrapperRef.current || !contentRef.current) return

    let gsapInstance: typeof import('gsap').default

    const animateExpansion = async () => {
      try {
        gsapInstance = (await import('gsap')).default

        const wrapper = wrapperRef.current
        const content = contentRef.current
        if (!wrapper || !content) return

        const isMobile = window.innerWidth < 768
        const bottomStart = isMobile ? 24 : 32
        const rightStart = isMobile ? 24 : 32
        const maxWidthStart = 375

        // Kill any existing animations
        gsapInstance.killTweensOf([wrapper, content])
        // Create timeline for expanded animation
        const tl = gsapInstance.timeline()

        if (isExpanded) {
          // Animate wrapper to expanded state
          tl.to(wrapper, {
            maxWidth: '100%',
            width: '100%',
            bottom: 0,
            right: 0,
            left: 0,
            duration: 0.4,
            ease: 'power2.out',
          })

          // Animate content layout for desktop
          if (!isMobile) {
            // Set flex direction immediately for layout
            // content.style.flexDirection = 'row'
            // content.style.alignItems = 'center'
            // content.style.justifyContent = 'space-between'

            tl.to(
              content,
              {
                duration: 0.5,
                ease: 'power2.out',
              },
              '-=0.5'
            )
          }
        } else {
          // Reset content layout immediately (for desktop)
          if (!isMobile) {
            // content.style.flexDirection = 'column'
            // content.style.alignItems = 'stretch'
            // content.style.justifyContent = 'flex-start'

            tl.to(
              content, {
                duration: 0.5,
                ease: 'power2.in',
              }, 
              '-=0.5'
            )
          }

          // Clear left property after position animation
          tl.set(wrapper, {
            clearProps: 'left',
          })

          // Animate wrapper to collapsed state
          tl.to(wrapper, {
            maxWidth: maxWidthStart,
            width: maxWidthStart,
            bottom: bottomStart,
            right: rightStart,
            duration: 0.5,
            ease: 'power2.in',
          })
        }
      } catch (error) {
        console.warn('GSAP not available:', error)
      }
    }

    animateExpansion()

    return () => {
      if (gsapInstance) {
        gsapInstance.killTweensOf([wrapperRef.current, contentRef.current])
      }
    }
  }, [isExpanded])

  return (
    <S.StickyCtaWrapper 
      ref={wrapperRef}
      isExpanded={isExpanded} 
      colorScheme={activeColorScheme as ColorSchemeTypes}
    >
      <S.StickyCtaContent
        ref={contentRef}
        isExpanded={isExpanded} 
        colorScheme={activeColorScheme as ColorSchemeTypes}
      >
        <S.StickyCtaInnerContainer isExpanded={isExpanded}>
          <S.StickyCtaTitle>
            <Heading
              {...title}
              colorScheme={activeColorScheme}
            />
          </S.StickyCtaTitle>
          <S.StickyCtaButton>
            <Button
              {...cta}
              variant="primary"
              inheritedColorScheme={activeColorScheme}
              iconEnd='arrow-right'
            />
          </S.StickyCtaButton>
        </S.StickyCtaInnerContainer>
      </S.StickyCtaContent>
    </S.StickyCtaWrapper>
  )
}

export default StickyCta

